import React, { useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, DatePicker, TimePicker, Popconfirm, message, Space, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import PageContainer from '@/components/PageContainer';
import {
  useTodos,
  useCreateTodo,
  useUpdateTodo,
  useDeleteTodo,
  useBulkImportTodos,
  type TodoItem,
} from '@/api/todos';

const PRIORITY_OPTS = [
  { label: '紧急', value: 'urgent' },
  { label: '重要', value: 'important' },
  { label: '普通', value: 'normal' },
];
const STATUS_OPTS = [
  { label: '待处理', value: 'pending' },
  { label: '进行中', value: 'ing' },
  { label: '已完成', value: 'done' },
  { label: '已延期', value: 'delay' },
];

const TAG_COLORS: Record<string, string> = {
  urgent: 'red', important: 'orange', normal: 'default',
  pending: 'default', ing: 'processing', done: 'success', delay: 'error',
};

const emptyItem = (): TodoItem => ({
  title: '', description: '', priority: 'normal',
  status: 'pending', owner: '', due_date: null, due_time: '', is_daily: false,
});

const TodoManagePage: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<TodoItem>(emptyItem());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkJson, setBulkJson] = useState('');

  const { data, isLoading } = useTodos();
  const createMu = useCreateTodo();
  const updateMu = useUpdateTodo();
  const deleteMu = useDeleteTodo();
  const bulkMu = useBulkImportTodos();

  const todos = (data?.data?.todos ?? []) as TodoItem[];

  const openCreate = () => {
    setEditing(emptyItem());
    setModalOpen(true);
  };
  const openEdit = (item: TodoItem) => {
    setEditing({ ...item });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!editing.title.trim()) {
      message.warning('标题不能为空');
      return;
    }
    if (editing.id) {
      await updateMu.mutateAsync(editing as TodoItem & { id: number });
    } else {
      await createMu.mutateAsync(editing);
    }
    setModalOpen(false);
    message.success('保存成功');
  };

  const handleBulkImport = async () => {
    try {
      const items = JSON.parse(bulkJson);
      if (!Array.isArray(items)) throw new Error('Not an array');
      await bulkMu.mutateAsync(items);
      setBulkOpen(false);
      setBulkJson('');
      message.success(`导入成功：${items.length} 条`);
    } catch {
      message.error('JSON 格式错误，需要数组');
    }
  };

  const columns = [
    { title: '标题', dataIndex: 'title', ellipsis: true },
    { title: '优先级', dataIndex: 'priority', width: 70, render: (v: string) => <Tag color={TAG_COLORS[v]}>{PRIORITY_OPTS.find(o => o.value === v)?.label || v}</Tag> },
    { title: '状态', dataIndex: 'status', width: 70, render: (v: string) => v ? <Tag color={TAG_COLORS[v]}>{STATUS_OPTS.find(o => o.value === v)?.label || v}</Tag> : '-' },
    { title: '负责人', dataIndex: 'owner', width: 80 },
    { title: '每日循环', dataIndex: 'is_daily', width: 70, render: (v: boolean) => v ? <Tag color="blue">是</Tag> : <Tag>否</Tag> },
    { title: '截止日期', dataIndex: 'due_date', width: 100, render: (v: string) => v || '-' },
    {
      title: '操作', width: 120,
      render: (_: unknown, record: TodoItem) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm title="确定删除?" onConfirm={() => deleteMu.mutate(record.id!)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <PageContainer title="待办管理">
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新增</Button>
        <Button icon={<UploadOutlined />} onClick={() => setBulkOpen(true)}>JSON 批量导入</Button>
      </div>

      <Table
        columns={columns}
        dataSource={todos}
        rowKey="id"
        loading={isLoading || createMu.isPending || updateMu.isPending || deleteMu.isPending}
        size="small"
        pagination={{ pageSize: 20 }}
      />

      {/* Edit modal */}
      <Modal
        title={editing.id ? '编辑' : '新增'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        width={520}
        confirmLoading={createMu.isPending || updateMu.isPending}
      >
        <Form layout="vertical" size="small">
          <Form.Item label="标题" required>
            <Input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} />
          </Form.Item>
          <Form.Item label="描述">
            <Input.TextArea rows={2} value={editing.description} onChange={e => setEditing({ ...editing, description: e.target.value })} />
          </Form.Item>
          <Space>
            <Form.Item label="优先级">
              <Select value={editing.priority} onChange={(v) => setEditing({ ...editing, priority: v })} options={PRIORITY_OPTS} style={{ width: 100 }} />
            </Form.Item>
            <Form.Item label="状态">
              <Select value={editing.status} onChange={(v) => setEditing({ ...editing, status: v })} options={STATUS_OPTS} style={{ width: 100 }} />
            </Form.Item>
            <Form.Item label="负责人">
              <Input value={editing.owner} onChange={e => setEditing({ ...editing, owner: e.target.value })} style={{ width: 100 }} />
            </Form.Item>
          </Space>
          <Space>
            {!editing.is_daily && (
              <Form.Item label="截止日期">
                <DatePicker
                  value={editing.due_date ? dayjs(editing.due_date) : null}
                  onChange={(d) => setEditing({ ...editing, due_date: d ? d.format('YYYY-MM-DD') : null })}
                />
              </Form.Item>
            )}
            <Form.Item label="时间">
              <TimePicker
                value={editing.due_time ? dayjs(editing.due_time, 'HH:mm') : null}
                onChange={(t) => setEditing({ ...editing, due_time: t ? t.format('HH:mm') : '' })}
                format="HH:mm" style={{ width: 100 }}
              />
            </Form.Item>
            <Form.Item label="模式">
              <Select value={editing.is_daily ? 'daily' : 'once'} onChange={(v) => setEditing({ ...editing, is_daily: v === 'daily', due_date: v === 'daily' ? null : editing.due_date })} options={[{label:'每日循环',value:'daily'},{label:'指定日期',value:'once'}]} style={{ width: 110 }} />
            </Form.Item>
          </Space>
        </Form>
      </Modal>

      {/* Bulk import modal */}
      <Modal
        title="JSON 批量导入"
        open={bulkOpen}
        onOk={handleBulkImport}
        onCancel={() => setBulkOpen(false)}
        confirmLoading={bulkMu.isPending}
      >
        <Input.TextArea
          rows={10}
          value={bulkJson}
          onChange={e => setBulkJson(e.target.value)}
          placeholder={`[\n  {"title":"电量申报截止","priority":"urgent","due_date":"2026-05-20","due_time":"14:00","is_daily":true},\n  {"title":"月度交易申报","priority":"important","due_date":"2026-05-15"}\n]`}
        />
      </Modal>
    </PageContainer>
  );
};

export default TodoManagePage;
